import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { MetaService } from '@app/shared/services/meta.service';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaRouterService } from '@app/akita/router/state/router.service';
import {
  logOnCreditCardSuccess,
  logPurchase,
  logPurchaseInsurance,
} from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaCheckoutService } from '@app/akita/api/checkout/state/checkout.service';
import { PaymentModel } from '@app/akita/api/checkout/models/payment.model';
import { Product } from '@app/akita/api/products/models/product.model';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import {
  PaymentMethod,
  PaymentProvider,
} from '@app/akita/api/checkout/models/payment.provider.model';
import { AsyncPipe } from '@angular/common';
import { LottieAnimationComponent } from '../../../components/lottie-animation/lottie-animation.component';
import { Insurance } from '@app/akita/api/products/models/insurance.model';
import { MSClarityService } from '@app/shared/services/msclarity.service';

export const PAYMENT_STATUS_FETCH_INTERVAL = 2000;
export const MAX_PAYMENT_STATUS_FETCH_RETRIES = 20;

@Component({
  selector: 'app-cko-callback',
  templateUrl: './cko-callback.template.html',
  styleUrls: ['./cko-callback.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, LottieAnimationComponent, AsyncPipe],
})
export class CheckoutDotComCallbackComponent implements OnDestroy, AfterViewInit {
  public isBrowser: boolean;
  public rtlMode$: Observable<boolean>;
  public lessThan$: Observable<ScreenSizeInfoState>;

  public retryPaymentStatusCounter = 0;
  private sessionProducts: Array<Product> = [];
  private sessionInsurances: Array<Insurance> = [];
  private hasBuyback = false;
  private isProtected = false;
  private isAbandoned: boolean | null = null;
  private isExpressCheckout: boolean | null = null;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly meta: MetaService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly translateService: TranslocoService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaRouterService: AkitaRouterService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaCheckoutService: AkitaCheckoutService,
    private readonly msClarityService: MSClarityService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.rtlMode$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();

    this.subscriptions.add(
      this.translateService.langChanges$.pipe(takeUntil(this.destroySubject)).subscribe({
        next: () => {
          const description = this.translateService.translate(
            'title_delivery_status_processing'
          );
          this.meta.setTitle('title_delivery_status_processing', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      })
    );
  }

  public ngAfterViewInit(): void {
    const viewStep = `${this.akitaRouterQuery.viewStep || ''}`.replace('CKO_', '');
    // const productId = this.akitaRouterQuery.paramsAndQueryParams['lid'] || '';
    // const ckoSessionId = this.akitaRouterQuery.queryParams['cko-session-id'] || '';
    const sessionId: string =
      this.akitaRouterQuery.queryParams.pid || this.akitaRouterQuery.params.pid || '';

    this.isAbandoned = Boolean(this.akitaRouterQuery.queryParams.is_abandoned) || null;
    this.isExpressCheckout =
      Boolean(this.akitaRouterQuery.queryParams.express_checkout) || null;

    if (this.isExpressCheckout) {
      this.msClarityService.setTag('payment-intent', 'EXPRESS_PURCHASE');
      console.log('Express Checkout:', this.isExpressCheckout);
    }

    if (viewStep === 'SUCCESS') {
      this.handlePaymentSuccess(sessionId);
    } else if (viewStep === 'FAIL') {
      this.handlePaymentError(sessionId);
    }
  }

  private handlePaymentSuccess(sessionId: string): void {
    logOnCreditCardSuccess(
      this.googleAnalyticsService.logEventWrapper,
      'CHECKOUT',
      this.akitaProductsQuery.country
    );

    this.subscriptions.add(
      this.akitaCheckoutService
        .fetchSession(sessionId)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (session) => {
            this.sessionProducts = session?.order || [];
            this.sessionInsurances = session?.insurances || [];
            this.hasBuyback = Boolean(session?.buyBackCart) || false;
            this.isProtected = Boolean(session?.protectionPrice) || false;
            this.fetchPaymentStatus(session?.id || sessionId, session?.orderId || null);
          },
          error: () => {},
        })
    );
  }

  private handlePaymentError(sessionId: string): void {
    this.navigateToPaymentErrorPage(sessionId);
  }

  private navigateToSuccessPage(sessionId: string | null): void {
    this.akitaCheckoutService.updateCheckoutStep('SUCCESS');
    const fragment = this.akitaRouterQuery.homeUrlFragment;

    if (sessionId) {
      fragment.push('checkout');
      fragment.push(sessionId);
      fragment.push('overview');
    }

    SentryUtil.addBreadcrumb({
      category: 'Payment Callback',
      message: `User redirected to success page from Checkout.com`,
      level: 'info',
      type: 'navigation',
    });

    this.akitaRouterService.navigate(
      fragment,
      {
        cko: 'success',
      },
      {
        replaceUrl: true,
      }
    );
  }

  private navigateToPaymentErrorPage(sessionId: string | null): void {
    this.akitaCheckoutService.updateCheckoutStep('FAILURE');
    const fragment = this.akitaRouterQuery.homeUrlFragment;

    console.log('navigateToPaymentErrorPage', sessionId);
    if (sessionId) {
      fragment.push('checkout');
      fragment.push(sessionId);
      fragment.push('overview');
    }

    SentryUtil.addBreadcrumb({
      category: 'Payment Callback',
      message: `User redirected to error page from Checkout.com`,
      level: 'info',
      type: 'navigation',
    });

    this.akitaRouterService.navigate(
      fragment,
      {
        cko: 'error',
      },
      {
        replaceUrl: true,
      }
    );
  }

  private fetchPaymentStatus(sessionId: string, orderId: string | null): void {
    this.zone.runOutsideAngular(() => {
      // TODO get order from API
      this.subscriptions.add(
        this.akitaCheckoutService
          .fetchPayment(orderId)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: (payment: PaymentModel | null) => {
              if (
                payment?.paymentState === 'APPROVED' ||
                payment?.paymentState === 'COMPLETE'
              ) {
                logPurchase(
                  this.googleAnalyticsService.logEventWrapper,
                  this.googleAnalyticsService.firebaseRecordPurchaseADConversion,
                  this.googleAnalyticsService.facebookTrackPurchase,
                  orderId || '?',
                  this.sessionProducts || [],
                  0,
                  0,
                  payment?.currency || 'SAR',
                  'redirect',
                  (payment?.paymentProvider as PaymentProvider) || null,
                  (payment?.paymentMethod as PaymentMethod) || null,
                  null,
                  this.isProtected,
                  this.isAbandoned,
                  this.isExpressCheckout,
                  this.hasBuyback
                );
                logPurchaseInsurance(
                  this.googleAnalyticsService.logEventWrapper,
                  orderId || '?',
                  this.sessionInsurances || [],
                  (payment?.paymentProvider as PaymentProvider) || null,
                  (payment?.paymentMethod as PaymentMethod) || null
                );
                this.navigateToSuccessPage(sessionId || null);
              } else if (
                (payment?.paymentState === 'PENDING' ||
                  payment?.paymentState === 'WAITING') &&
                this.retryPaymentStatusCounter < MAX_PAYMENT_STATUS_FETCH_RETRIES
              ) {
                if (this.akitaRouterQuery.isBrowser) {
                  setTimeout(() => {
                    this.retryPaymentStatusCounter += 1;
                    this.fetchPaymentStatus(sessionId, orderId);
                  }, PAYMENT_STATUS_FETCH_INTERVAL);
                }
              } else {
                this.navigateToPaymentErrorPage(sessionId || null);
              }
            },
            error: (err: unknown) => {
              SentryUtil.addBreadcrumb({
                category: 'Payment Callback',
                message: `Error while fetching payment status from Checkout.com`,
                level: 'error',
                type: 'network',
              });
              SentryUtil.reportException(err, false);
              this.navigateToPaymentErrorPage(sessionId || null);
            },
          })
      );
    });
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
